import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Truck, MapPin, RefreshCw, Check, Send, RotateCcw, X, Package, Trash2 } from 'lucide-react';

const API = '/api/delivery';

function fmtUsd(v) {
  const n = Number(v || 0);
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function nextDeliveryDate() {
  // Adam's cadence: Wednesdays & Fridays. Default to the next one.
  const d = new Date();
  const today = d.getDay();
  // 3 = Wed, 5 = Fri
  const target = [3, 5, 3, 3, 5, 5, 3][today]; // shorthand: move forward to next Wed/Fri
  const daysUntil =
    today < 3 ? 3 - today
    : today === 3 ? 0
    : today < 5 ? 5 - today
    : today === 5 ? 0
    : 3 + (7 - today);  // Sat/Sun → next Wed
  d.setDate(d.getDate() + daysUntil);
  return d.toISOString().slice(0, 10);
}

export default function DeliveryDispatch() {
  const [routeDate, setRouteDate]     = useState(nextDeliveryDate());
  const [driverName, setDriverName]   = useState('Tres Hirsch');
  const [unassigned, setUnassigned]   = useState([]);
  const [selected, setSelected]       = useState(new Set());
  const [route, setRoute]             = useState(null);
  const [stops, setStops]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [message, setMessage]         = useState(null);

  const loadUnassigned = useCallback(async () => {
    const { data } = await axios.get(`${API}/stops/unassigned`);
    setUnassigned(data);
  }, []);

  const loadRouteForDate = useCallback(async () => {
    const { data } = await axios.get(`${API}/routes`, { params: { date: routeDate } });
    const r = (data || [])[0];
    if (r) {
      setRoute(r);
      const detail = await axios.get(`${API}/routes/${r.id}`);
      setStops(detail.data.stops || []);
    } else {
      setRoute(null);
      setStops([]);
    }
  }, [routeDate]);

  useEffect(() => { loadUnassigned(); }, [loadUnassigned]);
  useEffect(() => { loadRouteForDate(); }, [loadRouteForDate]);

  const toggleSelected = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const flash = (msg, tone = 'ok') => {
    setMessage({ msg, tone });
    setTimeout(() => setMessage(null), 4000);
  };

  const syncNetsuite = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/sync-netsuite`);
      flash(`Synced: ${data.added} new, ${data.updated} updated, ${data.total} total`);
      await loadUnassigned();
    } catch (err) {
      flash(err.response?.data?.error || err.message, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const buildRoute = async () => {
    if (selected.size === 0) return flash('Select stops first', 'error');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/routes`, {
        routeDate,
        driverName: driverName || null,
        stopIds: Array.from(selected),
      });
      flash(`Route optimized: ${data.optimized.distanceMiles} mi, ~${data.optimized.durationMin} min`);
      setSelected(new Set());
      await Promise.all([loadUnassigned(), loadRouteForDate()]);
    } catch (err) {
      flash(err.response?.data?.error || err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const reoptimize = async () => {
    if (!route) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/routes/${route.id}/reoptimize`);
      flash(`Reoptimized: ${data.optimized.distanceMiles} mi, ~${data.optimized.durationMin} min`);
      await loadRouteForDate();
    } catch (err) {
      flash(err.response?.data?.error || err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const dispatch = async () => {
    if (!route) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/routes/${route.id}/dispatch`);
      flash(`Dispatched — ${data.dispatched} confirmation email(s) sent`);
      await loadRouteForDate();
    } catch (err) {
      flash(err.response?.data?.error || err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const removeStop = async (stopId) => {
    if (!route) return;
    await axios.delete(`${API}/routes/${route.id}/stops/${stopId}`);
    await Promise.all([loadUnassigned(), loadRouteForDate()]);
  };

  const dismissStop = async (stopId, defaultReason = 'Shipped via Track-POD') => {
    const reason = window.prompt('Dismiss this stop? Enter a reason:', defaultReason);
    if (reason == null) return; // cancelled
    try {
      await axios.delete(`${API}/stops/${stopId}`, { params: { reason: reason || defaultReason } });
      setSelected(prev => {
        const next = new Set(prev);
        next.delete(stopId);
        return next;
      });
      await Promise.all([loadUnassigned(), loadRouteForDate()]);
      flash('Stop dismissed');
    } catch (err) {
      flash(err.response?.data?.error || err.message, 'error');
    }
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Truck size={28} color="#8B6F4E" />
        <h1 style={{ margin: 0, color: '#2b2b2b', fontSize: 24, fontWeight: 600 }}>Local Delivery Dispatch</h1>
      </div>

      {/* Controls */}
      <div style={{ background: '#fff', border: '1px solid #e5e5e0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Route date">
            <input
              type="date"
              value={routeDate}
              onChange={e => setRouteDate(e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Driver name (optional)">
            <input
              type="text"
              value={driverName}
              onChange={e => setDriverName(e.target.value)}
              placeholder="e.g. Adam"
              style={{ ...inputStyle, minWidth: 220 }}
            />
          </Field>
          <button onClick={syncNetsuite} disabled={syncing} style={btnSecondary}>
            <RefreshCw size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
            {syncing ? 'Syncing…' : 'Pull from NetSuite'}
          </button>
        </div>
      </div>

      {message && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 16,
          background: message.tone === 'error' ? '#fff3f3' : '#f3f8f0',
          color:      message.tone === 'error' ? '#a03030' : '#2b5a2b',
          border:     `1px solid ${message.tone === 'error' ? '#e5c0c0' : '#cddfc0'}`,
        }}>{message.msg}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Unassigned */}
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>
            <h2 style={h2Style}>Unassigned local-delivery orders</h2>
            <span style={{ color: '#888', fontSize: 13 }}>{unassigned.length} pending</span>
          </div>
          {unassigned.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>
              No pending local-delivery orders. Try "Pull from NetSuite".
            </div>
          ) : (
            <div>
              {unassigned.map(s => (
                <div key={s.id} style={{
                  display: 'flex', gap: 12, padding: '12px 16px',
                  borderBottom: '1px solid #f0f0ea',
                  background: selected.has(s.id) ? '#fbf7f2' : 'transparent',
                }}>
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggleSelected(s.id)}
                    style={{ marginTop: 4, cursor: 'pointer' }}
                  />
                  <label
                    htmlFor={`stop-${s.id}`}
                    onClick={() => toggleSelected(s.id)}
                    style={{ flex: 1, cursor: 'pointer' }}
                  >
                    <div style={{ fontWeight: 600, color: '#2b2b2b' }}>
                      {s.order_number} · {s.customer_name}
                    </div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                      <MapPin size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                      {[s.address_line1, s.city, s.state, s.zip].filter(Boolean).join(', ')}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {s.box_count != null ? (
                        <><Package size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{s.box_count} {s.box_count === 1 ? 'box' : 'boxes'} · </>
                      ) : null}
                      {s.item_count} items · {fmtUsd(s.order_total)}
                      {s.customer_email ? ` · ${s.customer_email}` : ' · no email'}
                    </div>
                  </label>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismissStop(s.id); }}
                    title="Dismiss (e.g. already shipped via Track-POD)"
                    style={iconBtn}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <div style={{ padding: 16, borderTop: '1px solid #f0f0ea' }}>
                <button
                  onClick={buildRoute}
                  disabled={loading || selected.size === 0}
                  style={btnPrimary}
                >
                  <Check size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
                  Build route from {selected.size} selected
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Today's route */}
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>
            <h2 style={h2Style}>Route for {routeDate}</h2>
            {route && (
              <span style={{ color: '#888', fontSize: 13 }}>
                {route.total_stops} stops · {route.total_distance_miles || '--'} mi · ~{route.total_duration_min || '--'} min
              </span>
            )}
          </div>
          {!route ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>
              No route yet. Select stops on the left and click "Build route".
            </div>
          ) : (
            <>
              <div style={{ padding: '12px 16px', background: '#fbf7f2', fontSize: 13, color: '#6b5540' }}>
                Status: <strong>{route.status}</strong>
                {route.driver_name ? ` · Driver: ${route.driver_name}` : ''}
              </div>
              {stops.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>No stops on this route.</div>
              ) : (
                <>
                  <RouteMap routeId={route.id} />
                  {stops.map(s => (
                    <div key={s.id} style={{
                      padding: '12px 16px', borderBottom: '1px solid #f0f0ea',
                      display: 'flex', gap: 12,
                    }}>
                      <div style={{
                        minWidth: 28, height: 28, borderRadius: '50%',
                        background: '#8B6F4E', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 600,
                      }}>{s.sequence || '·'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>
                          {s.order_number} · {s.customer_name}
                          <StatusPill status={s.status} />
                        </div>
                        <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                          {[s.address_line1, s.city, s.state, s.zip].filter(Boolean).join(', ')}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                          {s.box_count != null && (
                            <><Package size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{s.box_count} {s.box_count === 1 ? 'box' : 'boxes'} · </>
                          )}
                          {s.item_count} items
                        </div>
                      </div>
                      {s.status === 'scheduled' && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => removeStop(s.id)} title="Remove from route (keep in unassigned)" style={iconBtn}>
                            <X size={16} />
                          </button>
                          <button onClick={() => dismissStop(s.id)} title="Dismiss (already shipped via Track-POD)" style={iconBtn}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
              <div style={{ padding: 16, borderTop: '1px solid #f0f0ea', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={reoptimize} disabled={loading} style={btnSecondary}>
                  <RotateCcw size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
                  Reoptimize
                </button>
                <button
                  onClick={dispatch}
                  disabled={loading || route.status === 'dispatched' || route.status === 'in_progress' || route.status === 'completed'}
                  style={btnPrimary}
                >
                  <Send size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
                  {route.status === 'dispatched' ? 'Already dispatched' : 'Dispatch & email customers'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Server-rendered static map of the optimized route.
 * Fetched as a blob so the Hub auth token rides along with the request
 * (browser <img> tags can't add Authorization headers).
 */
function RouteMap({ routeId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(null);
  const lastUrl = useRef(null);

  useEffect(() => {
    if (!routeId) return;
    let cancelled = false;
    setErr(null);
    axios.get(`${API}/routes/${routeId}/map.png`, { responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
        const u = URL.createObjectURL(r.data);
        lastUrl.current = u;
        setSrc(u);
      })
      .catch(e => {
        if (!cancelled) setErr(e.response?.status === 503 ? 'Map preview requires GOOGLE_MAPS_API_KEY' : 'Map unavailable');
      });
    return () => {
      cancelled = true;
      if (lastUrl.current) { URL.revokeObjectURL(lastUrl.current); lastUrl.current = null; }
    };
  }, [routeId]);

  if (err) return null;
  if (!src) return <div style={{ height: 220, background: '#f5f3ee', borderBottom: '1px solid #f0f0ea' }} />;
  return (
    <div style={{ borderBottom: '1px solid #f0f0ea' }}>
      <img src={src} alt="Route preview" style={{ width: '100%', display: 'block' }} />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    scheduled: { bg: '#f0eee8', fg: '#6b5540' },
    en_route:  { bg: '#fef3c7', fg: '#854d0e' },
    arrived:   { bg: '#dbeafe', fg: '#1e40af' },
    delivered: { bg: '#d1fae5', fg: '#065f46' },
    failed:    { bg: '#fee2e2', fg: '#991b1b' },
    skipped:   { bg: '#f3f4f6', fg: '#6b7280' },
  };
  const c = map[status] || map.scheduled;
  return (
    <span style={{
      marginLeft: 8, padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
      background: c.bg, color: c.fg,
    }}>{status}</span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b5540', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  padding: '8px 12px', border: '1px solid #d8d4ca', borderRadius: 6,
  fontSize: 14, background: '#fff',
};
const panelStyle = { background: '#fff', border: '1px solid #e5e5e0', borderRadius: 12, overflow: 'hidden' };
const panelHeaderStyle = {
  padding: '14px 16px', borderBottom: '1px solid #f0f0ea',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const h2Style = { margin: 0, fontSize: 15, fontWeight: 600, color: '#2b2b2b' };
const btnPrimary = {
  padding: '8px 16px', background: '#8B6F4E', color: '#fff',
  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer',
};
const btnSecondary = {
  padding: '8px 16px', background: '#fff', color: '#6b5540',
  border: '1px solid #d8d4ca', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer',
};
const iconBtn = {
  padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#999',
};
